import React, { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import GoalButton from "./GoalButton";
import GoalPill from "./GoalPill";
import GoalActionButton from "./GoalActionButton";
import SuiInput from "components/SuiInput";
import CustomSelect from "components/CustomSelect";
import goalSelectStyles from "./goalSelectStyles";
import {
  GOAL_CATEGORY_LIST,
  CATEGORY_FIELD_MAP,
  percentageSum,
  approxEqual,
  stripNegative,
  noSpinnerSx,
} from "../goals.constants";

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = [currentYear - 1, currentYear, currentYear + 1];

const emptyPercentages = {
  client_satisfaction_percentage: 0,
  professional_goals_percentage: 0,
  personal_goals_percentage: 0,
};

// Inline form on the engineer details page. Also doubles as the edit form: pass
// `existingGoal` (+ `onUpdate`) to edit an already-set-up period in place -
// setup is editable at any status, any time. `onDone` fires after a successful
// save, `onCancel` when the admin backs out.
// Category/total percentages are fixed by the chosen template and are never
// editable here - a different split means a different template.
function GoalSetupForm({
  engineer,
  templates,
  onSubmit,
  existingGoal,
  onUpdate,
  onConfirmSetup,
  onCancel,
  onDone,
}) {
  const isEditing = !!existingGoal;
  const templateOptions = templates.map((template) => ({
    value: template.id,
    label: `${template.label} (${template.total_percentage}%)`,
  }));
  const yearOptions = YEAR_OPTIONS.map((y) => ({ value: y, label: String(y) }));
  const isDraft = isEditing && existingGoal.status === "draft";

  const [templateId, setTemplateId] = useState("");
  const [year, setYear] = useState(currentYear);
  const [percentages, setPercentages] = useState(emptyPercentages);
  const [goalsByCategory, setGoalsByCategory] = useState({});
  const [localError, setLocalError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedTemplate = templates.find((t) => String(t.id) === String(templateId));

  useEffect(() => {
    setLocalError(null);

    if (isEditing) {
      setTemplateId(existingGoal.fk_goal_period_template_id);
      setYear(existingGoal.year);
      setPercentages({
        client_satisfaction_percentage: existingGoal.client_satisfaction_percentage,
        professional_goals_percentage: existingGoal.professional_goals_percentage,
        personal_goals_percentage: existingGoal.personal_goals_percentage,
      });
      const grouped = Object.fromEntries(GOAL_CATEGORY_LIST.map((category) => [category, []]));
      (existingGoal.goals || []).forEach((goal) => {
        grouped[goal.category] = [
          ...(grouped[goal.category] || []),
          {
            description: goal.description,
            weight_percentage: goal.weight_percentage,
            achieved_percentage: goal.achieved_percentage,
            review_notes: goal.review_notes,
          },
        ];
      });
      setGoalsByCategory(grouped);
    } else {
      setTemplateId("");
      setYear(currentYear);
      setPercentages(emptyPercentages);
      setGoalsByCategory(Object.fromEntries(GOAL_CATEGORY_LIST.map((category) => [category, []])));
    }
  }, [existingGoal?.id]);

  const handleTemplateChange = (id) => {
    setTemplateId(id);
    const template = templates.find((t) => String(t.id) === String(id));
    if (template) {
      setPercentages({
        client_satisfaction_percentage: template.client_satisfaction_percentage,
        professional_goals_percentage: template.professional_goals_percentage,
        personal_goals_percentage: template.personal_goals_percentage,
      });
    }
  };

  const addGoal = (category) => {
    setGoalsByCategory({
      ...goalsByCategory,
      [category]: [
        ...(goalsByCategory[category] || []),
        { description: "", weight_percentage: "" },
      ],
    });
  };

  const updateGoal = (category, index, field, value) => {
    const updated = [...(goalsByCategory[category] || [])];
    updated[index] = { ...updated[index], [field]: value };
    setGoalsByCategory({ ...goalsByCategory, [category]: updated });
  };

  const removeGoal = (category, index) => {
    const updated = [...(goalsByCategory[category] || [])];
    updated.splice(index, 1);
    setGoalsByCategory({ ...goalsByCategory, [category]: updated });
  };

  const totalPercentage = percentageSum(Object.values(percentages));
  const showEditor = isEditing || !!selectedTemplate;

  const buildGoalsAndValidate = () => {
    const allGoals = GOAL_CATEGORY_LIST.flatMap((category) =>
      (goalsByCategory[category] || []).map((goal) => ({ ...goal, category }))
    );

    if (allGoals.some((goal) => !goal.description || !goal.description.trim())) {
      setLocalError("Every goal needs a description.");
      return null;
    }

    for (const category of GOAL_CATEGORY_LIST) {
      const field = CATEGORY_FIELD_MAP[category];
      const weightSum = percentageSum(
        (goalsByCategory[category] || []).map((g) => g.weight_percentage)
      );
      if (!approxEqual(weightSum, percentages[field])) {
        setLocalError(
          `${category} goal weights (${weightSum}%) must sum to its allocated ${percentages[field]}%.`
        );
        return null;
      }
    }

    return allGoals.map((goal) => ({
      category: goal.category,
      description: goal.description.trim(),
      weight_percentage: Number(goal.weight_percentage || 0),
      achieved_percentage: goal.achieved_percentage ?? null,
      review_notes: goal.review_notes ?? null,
    }));
  };

  // `activate` (draft only) also confirms the setup right after saving, which
  // moves the period draft -> active. Without it the period stays a draft.
  const handleSave = async (activate = false) => {
    setLocalError(null);
    const goals = buildGoalsAndValidate();
    if (!goals) return;

    setIsSubmitting(true);
    let result = await onUpdate(existingGoal.id, { goals });
    if (result && activate) result = await onConfirmSetup(existingGoal.id);
    setIsSubmitting(false);
    if (result) onDone();
  };

  const handleSubmit = async (directSetup) => {
    setLocalError(null);

    if (!selectedTemplate) {
      setLocalError("Please select a period template.");
      return;
    }

    const goals = buildGoalsAndValidate();
    if (!goals) return;

    const body = {
      fk_user_channel_id: engineer.user_channel_id,
      fk_goal_period_template_id: selectedTemplate.id,
      year: Number(year),
      goals,
      direct_setup: directSetup,
    };

    setIsSubmitting(true);
    const result = await onSubmit(body);
    setIsSubmitting(false);
    if (result) onDone();
  };

  return (
    <Card sx={{ mb: 3 }}>
      <SuiBox p={3}>
        <SuiTypography variant="h6" fontWeight="bold" mb={2}>
          {isEditing ? "Edit Goals" : "Set Goals"} — {engineer?.display_name || engineer?.name}
        </SuiTypography>

        {localError && (
          <SuiTypography
            variant="caption"
            display="block"
            mb={1}
            sx={{ color: "#c01616 !important", fontWeight: 500 }}
          >
            {localError}
          </SuiTypography>
        )}

        <Grid container spacing={2} mb={2}>
          <Grid item xs={8}>
            <SuiTypography variant="caption">Period Template</SuiTypography>
            {isEditing ? (
              <SuiTypography variant="button" fontWeight="medium" display="block">
                {existingGoal.template?.label || "Custom period"} ({existingGoal.start_date} —{" "}
                {existingGoal.end_date})
              </SuiTypography>
            ) : (
              <CustomSelect
                customStyles={goalSelectStyles}
                placeholder="Select a template…"
                options={templateOptions}
                value={templateOptions.find((o) => String(o.value) === String(templateId)) || null}
                onChange={(option) => handleTemplateChange(option ? option.value : "")}
                menuPortalTarget={document.body}
              />
            )}
          </Grid>
          <Grid item xs={4}>
            <SuiTypography variant="caption">Year</SuiTypography>
            {isEditing ? (
              <SuiTypography variant="button" fontWeight="medium" display="block">
                {year}
              </SuiTypography>
            ) : (
              <CustomSelect
                customStyles={goalSelectStyles}
                options={yearOptions}
                value={yearOptions.find((o) => o.value === Number(year))}
                onChange={(option) => setYear(option.value)}
                menuPortalTarget={document.body}
              />
            )}
          </Grid>
        </Grid>

        {showEditor &&
          GOAL_CATEGORY_LIST.map((category) => {
            const field = CATEGORY_FIELD_MAP[category];
            const weightSum = percentageSum(
              (goalsByCategory[category] || []).map((g) => g.weight_percentage)
            );
            const matches = approxEqual(weightSum, percentages[field]);

            return (
              <SuiBox
                key={category}
                mb={2}
                p={2}
                sx={{ border: "1px solid #eee", borderRadius: 2 }}
              >
                <SuiBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <SuiTypography variant="button" fontWeight="medium">
                    {category}
                  </SuiTypography>
                  <GoalPill color={matches ? "success" : "error"}>
                    {weightSum}% / {percentages[field]}% allocated
                  </GoalPill>
                </SuiBox>

                {(goalsByCategory[category] || []).map((goal, index) => (
                  <Grid container spacing={1} key={index} mb={1} alignItems="center">
                    <Grid item xs={8}>
                      <SuiInput
                        placeholder="Goal description"
                        value={goal.description}
                        onChange={(e) => updateGoal(category, index, "description", e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <SuiInput
                        type="number"
                        sx={noSpinnerSx}
                        placeholder="Weight %"
                        inputProps={{ min: 0, onWheel: (e) => e.target.blur() }}
                        value={goal.weight_percentage}
                        onChange={(e) =>
                          updateGoal(
                            category,
                            index,
                            "weight_percentage",
                            stripNegative(e.target.value)
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={1}>
                      <GoalActionButton
                        icon="delete_outline"
                        tooltip="Remove goal"
                        color="error"
                        onClick={() => removeGoal(category, index)}
                      />
                    </Grid>
                  </Grid>
                ))}

                <SuiBox display="flex" alignItems="center" mt={1}>
                  <GoalButton
                    variant="text"
                    size="small"
                    icon="add"
                    onClick={() => addGoal(category)}
                  >
                    Add Goal
                  </GoalButton>
                </SuiBox>
              </SuiBox>
            );
          })}

        {showEditor && (
          <SuiTypography variant="button" fontWeight="medium" display="block" mb={2}>
            Total: {totalPercentage}%
          </SuiTypography>
        )}

        <SuiBox display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
          <GoalButton variant="secondary" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </GoalButton>
          {isEditing ? (
            <>
              {isDraft && (
                <GoalButton
                  icon="save"
                  loading={isSubmitting}
                  variant="tonal"
                  onClick={() => handleSave(false)}
                  disabled={isSubmitting}
                >
                  Save as Draft
                </GoalButton>
              )}
              <GoalButton
                icon="check"
                loading={isSubmitting}
                variant="primary"
                onClick={() => handleSave(isDraft)}
                disabled={isSubmitting}
              >
                Save Changes
              </GoalButton>
            </>
          ) : (
            <>
              <GoalButton
                icon="save"
                loading={isSubmitting}
                variant="tonal"
                onClick={() => handleSubmit(false)}
                disabled={isSubmitting || !selectedTemplate}
              >
                Save as Draft
              </GoalButton>
              <GoalButton
                icon="check"
                loading={isSubmitting}
                variant="primary"
                onClick={() => handleSubmit(true)}
                disabled={isSubmitting || !selectedTemplate}
              >
                Set Up Goals
              </GoalButton>
            </>
          )}
        </SuiBox>
        {!isEditing && (
          <SuiTypography
            variant="caption"
            color="secondary"
            display="block"
            mt={1}
            textAlign="right"
          >
            Draft needs a confirmation step before it's active. &quot;Set Up Goals&quot; skips that
            and activates immediately.
          </SuiTypography>
        )}
        {isDraft && (
          <SuiTypography
            variant="caption"
            color="secondary"
            display="block"
            mt={1}
            textAlign="right"
          >
            Goals can&apos;t be marked while this is a draft. &quot;Save Changes&quot; saves and
            activates it; &quot;Save as Draft&quot; keeps it as a draft.
          </SuiTypography>
        )}
      </SuiBox>
    </Card>
  );
}

export default GoalSetupForm;
